/**
 * Shared shipping constants — importable from BOTH client (webpack
 * handles the CommonJS interop) and server (Node `require` directly).
 *
 * This file MUST stay pure strings/objects with no React, image, or
 * environment-specific imports. The whole point of moving it out of
 * src/util/enums.js was that enums.js loaded image assets at the top,
 * which Node can't process.
 *
 * If you find yourself wanting to add a new constant that needs an
 * asset or context: add it to src/util/enums.js instead (client-only).
 */

// ---------------------------------------------------------------------------
// SHIPPING SCOPE
// ---------------------------------------------------------------------------
const DOMESTIC = 'Domestic';
const INTERNATIONAL = 'International';

// ---------------------------------------------------------------------------
// SHIPPING COST TYPES
// ---------------------------------------------------------------------------
const FLAT_RATE = 'flat-rate';
const CALCULATED = 'calculated';

// ---------------------------------------------------------------------------
// CART OPERATIONS
// ---------------------------------------------------------------------------
const ADD = 'add';
const SUBTRACT = 'subtract';

// ---------------------------------------------------------------------------
// EASYPOST SERVICE LEVELS
// See https://www.easypost.com/docs/api#service-levels
// ---------------------------------------------------------------------------

// USPS
const MEDIAMAIL = 'mediamail';
const USPS_PRIORITY = 'priority';
const GROUND_ADVANTAGE = 'groundadvantage';
const FIRST_CLASS_PACKAGE_INTERNATIONAL_SERVICE = 'firstclasspackageinternationalservice';
const PRIORITY_MAIL_INTERNATIONAL = 'prioritymailinternational';
const EXPRESS_MAIL_INTERNATIONAL = 'expressmailinternational';

// FedEx
const SMART_POST = 'smart_post';
const STANDARD_OVERNIGHT = 'standard_overnight';
const PRIORITY_OVERNIGHT = 'priority_overnight';
const FEDEX_2_DAY = 'fedex_2_day';
const FEDEX_GROUND = 'fedex_ground';
const FEDEX_EXPRESS_SAVER = 'fedex_express_saver';
const EXPRESS = 'express'; // USPS Priority Express OR UPS Worldwide Express (distinguished by carrier)
const FEDEX_INTERNATIONAL_CONNECT_PLUS = 'fedex_international_connect_plus';
const INTERNATIONAL_ECONOMY = 'international_economy';
const FEDEX_INTERNATIONAL_PRIORITY = 'fedex_international_priority';

// UPS
const NEXT_DAY_AIR = 'nextdayair';
const NEXT_DAY_AIR_SAVER = 'nextdayairsaver';
const SECOND_DAY_AIR = '2nddayair';
const THREE_DAY_SELECT = '3dayselect';
const GROUND = 'ground';
const EXPEDITED = 'expedited';
const UPS_SAVER = 'upssaver';

// DHL (international — Good/Better/Best + small-parcel edge case)
const DHL_EXPRESS_WORLDWIDE_NONDOC = 'expressworldwidenondoc';
const DHL_PARCEL_INTERNATIONAL_DIRECT = 'dhlparcelinternationaldirect';
const DHL_PARCEL_INTERNATIONAL_STANDARD = 'dhlparcelinternationalstandard';
const DHL_PACKET_INTERNATIONAL = 'dhlpacketinternational';

// Canada Post (mostly relevant for Canadian sellers; US sellers ship via
// USPS instead). Values are lowercase because CarrierModalContent
// matches against `service.name.toLowerCase()` when building its
// service-lookup map — same convention as the USPS / FedEx constants
// above. EasyPost's actual service identifiers are PascalCase
// (RegularParcel, ExpeditedParcel, etc.); the toLowerCase() in the
// lookup means we store the lowercase form here.
const CANADA_POST_REGULAR_PARCEL = 'regularparcel';
const CANADA_POST_EXPEDITED_PARCEL = 'expeditedparcel';
const CANADA_POST_XPRESSPOST = 'xpresspost';
const CANADA_POST_PRIORITY = 'priorityovernight';
const CANADA_POST_TRACKED_PACKET_USA = 'trackedpacketusa';
const CANADA_POST_EXPEDITED_PARCEL_USA = 'expeditedparcelusa';
const CANADA_POST_XPRESSPOST_USA = 'xpresspostusa';
const CANADA_POST_PRIORITY_WORLDWIDE = 'priorityworldwide';
const CANADA_POST_TRACKED_PACKET_INTERNATIONAL = 'trackedpacketinternational';
const CANADA_POST_XPRESSPOST_INTERNATIONAL = 'xpresspostinternational';

// ---------------------------------------------------------------------------
// SERVICE GROUP LABELS (UI categorisation)
// ---------------------------------------------------------------------------
const ECONOMY_SERVICES = 'Economy services';
const STANDARD_SERVICES = 'Standard services';
const EXPEDITED_SERVICES = 'Expedited services';
const ONE_DAY_SERVICE = 'One day service';
const INTERNATIONAL_SERVICES = 'International services';

// ---------------------------------------------------------------------------
// CARRIER IDENTIFIERS
// ---------------------------------------------------------------------------
const USPS = 'usps';
const FEDEX = 'fedex';
const UPS = 'ups';

// ---------------------------------------------------------------------------
// DELIVERY METHODS
// ---------------------------------------------------------------------------
const DeliveryMethod = Object.freeze({
  PICKUP: 'pickup',
  SHIPPING: 'shipping',
});

// ---------------------------------------------------------------------------
// LINE-ITEM CODES
// ---------------------------------------------------------------------------
const LINE_ITEM_HANDLING_COST = 'line-item/handling-cost';

module.exports = {
  // Scope
  DOMESTIC,
  INTERNATIONAL,
  // Cost types
  FLAT_RATE,
  CALCULATED,
  // Cart
  ADD,
  SUBTRACT,
  // EasyPost services
  MEDIAMAIL,
  USPS_PRIORITY,
  GROUND_ADVANTAGE,
  FIRST_CLASS_PACKAGE_INTERNATIONAL_SERVICE,
  PRIORITY_MAIL_INTERNATIONAL,
  EXPRESS_MAIL_INTERNATIONAL,
  SMART_POST,
  STANDARD_OVERNIGHT,
  PRIORITY_OVERNIGHT,
  FEDEX_2_DAY,
  FEDEX_GROUND,
  FEDEX_EXPRESS_SAVER,
  EXPRESS,
  FEDEX_INTERNATIONAL_CONNECT_PLUS,
  INTERNATIONAL_ECONOMY,
  FEDEX_INTERNATIONAL_PRIORITY,
  NEXT_DAY_AIR,
  NEXT_DAY_AIR_SAVER,
  SECOND_DAY_AIR,
  THREE_DAY_SELECT,
  GROUND,
  EXPEDITED,
  UPS_SAVER,
  DHL_EXPRESS_WORLDWIDE_NONDOC,
  DHL_PARCEL_INTERNATIONAL_DIRECT,
  DHL_PARCEL_INTERNATIONAL_STANDARD,
  DHL_PACKET_INTERNATIONAL,
  CANADA_POST_REGULAR_PARCEL,
  CANADA_POST_EXPEDITED_PARCEL,
  CANADA_POST_XPRESSPOST,
  CANADA_POST_PRIORITY,
  CANADA_POST_TRACKED_PACKET_USA,
  CANADA_POST_EXPEDITED_PARCEL_USA,
  CANADA_POST_XPRESSPOST_USA,
  CANADA_POST_PRIORITY_WORLDWIDE,
  CANADA_POST_TRACKED_PACKET_INTERNATIONAL,
  CANADA_POST_XPRESSPOST_INTERNATIONAL,
  // Service groups
  ECONOMY_SERVICES,
  STANDARD_SERVICES,
  EXPEDITED_SERVICES,
  ONE_DAY_SERVICE,
  INTERNATIONAL_SERVICES,
  // Carrier identifiers
  USPS,
  FEDEX,
  UPS,
  // Delivery
  DeliveryMethod,
  // Line items
  LINE_ITEM_HANDLING_COST,
};
